import { useCallback } from 'react';

import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useDispatch, useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  ActualIncomeDocumentReadModelGrid,
  ActualIncomeDocumentReadModelSlim,
  ActualIncomeTotals,
  DailyIncomesByDate,
  DailyIncomesByDateParams,
  MeteringPointActualIncomeAggregatedReadModel,
  MeteringPointActualIncomeQueryModel,
  MeteringPointActualIncomeValidateModel,
  MeteringPointActualIncomesDocument,
  MeteringPointActualIncomesDocumentsParams,
  MeteringPointDailyActualIncomeCreateModel,
  MeteringPointDailyActualIncomeQueryModel,
  MeteringPointDailyActualIncomeReadModel,
  MeteringPointDailyActualIncomeUpdateModel,
  MeteringPointDailyIncomes,
  MeteringPointsActualIncomeQueryModel,
  SaveDailyIncomesPayload,
  UpdateDailyIncomesPayload,
  VerifyResponse,
} from './types';

export const meteringPointActualIncomeApi = createApi({
  reducerPath: 'meteringPointActualIncomeApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/metering-point-actual-incomes`,
  }),
  tagTypes: [
    'Document',
    'MeteringPoint',
    'DailyIncomes',
    'DailyIncome',
    'MeteringPoints',
    'MeteringPointsTotal',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    queryByMeteringPoints: builder.query<
      ActualIncomeDocumentReadModelSlim[],
      MeteringPointsActualIncomeQueryModel
    >({
      query: model => ({
        url: '/query',
        method: 'POST',
        data: model,
      }),
      providesTags: () => ['MeteringPoints'],
    }),
    getMeteringPointActualIncomesI: builder.infiniteQuery<
      ActualIncomeDocumentReadModelGrid,
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['MeteringPoint', 'MeteringPoints'],
    }),
    getMeteringPointActualIncomesTotal: builder.query<ActualIncomeTotals, any>({
      query: params => {
        return {
          url: `/query/totals`,
          params,
        };
      },
      providesTags: () => ['MeteringPointsTotal'],
    }),
    queryByMeteringPoint: builder.query<
      MeteringPointActualIncomeAggregatedReadModel,
      MeteringPointActualIncomeQueryModel
    >({
      query: model => ({
        url: '/metering-points/query',
        method: 'POST',
        data: model,
      }),
      providesTags: () => ['MeteringPoint'],
    }),
    getMeteringPointDailyActualIncome: builder.query<
      MeteringPointDailyActualIncomeReadModel,
      MeteringPointDailyActualIncomeQueryModel
    >({
      query: params => ({
        url: '/metering-points/daily-income',
        params,
      }),
    }),
    addMeteringPointDailyActualIncome: builder.mutation<
      string,
      MeteringPointDailyActualIncomeCreateModel
    >({
      query: params => ({
        url: '/metering-points/daily-income',
        method: 'POST',
        params,
      }),
    }),
    updateMeteringPointDailyActualIncome: builder.mutation<
      void,
      MeteringPointDailyActualIncomeUpdateModel
    >({
      query: ({ id, ...params }) => ({
        url: `/metering-points/daily-income/${id}`,
        method: 'PUT',
        params,
      }),
    }),
    validateActualIncome: builder.mutation<
      void,
      MeteringPointActualIncomeValidateModel
    >({
      query: data => ({
        url: '/validate',
        method: 'POST',
        data,
      }),
    }),
    getActualIncomeDocument: builder.query<
      MeteringPointActualIncomesDocument,
      MeteringPointActualIncomesDocumentsParams
    >({
      query: params => ({
        url: '/documents',
        method: 'GET',
        params,
      }),
      providesTags: ['Document'],
    }),
    getMeteringPointDailyIncomes: builder.query<
      MeteringPointDailyIncomes,
      MeteringPointActualIncomeQueryModel
    >({
      query: data => ({
        url: '/metering-points/query',
        method: 'POST',
        data,
      }),
      providesTags: ['DailyIncomes'],
    }),
    verifyActualIncome: builder.mutation<VerifyResponse, string>({
      query: id => ({
        url: `/documents/${id}/verify`,
        method: 'PUT',
      }),
      invalidatesTags: ['Document', 'MeteringPoints'],
    }),
    getDailyIncomesByDate: builder.query<
      DailyIncomesByDate,
      DailyIncomesByDateParams
    >({
      query: params => ({
        url: '/metering-points/daily-incomes',
        params,
      }),
      providesTags: ['DailyIncome'],
    }),
    saveDailyIncomes: builder.mutation<string, SaveDailyIncomesPayload>({
      query: data => ({
        url: '/metering-points/daily-incomes',
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'Document',
        'MeteringPoint',
        'DailyIncomes',
        'DailyIncome',
        'MeteringPoints',
      ],
    }),
    updateDailyIncomes: builder.mutation<string, UpdateDailyIncomesPayload>({
      query: data => ({
        url: `/metering-points/daily-incomes/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: [
        'Document',
        'MeteringPoint',
        'DailyIncomes',
        'DailyIncome',
        'MeteringPoints',
      ],
    }),
    fillByOperationalIncomes: builder.mutation<
      void,
      MeteringPointActualIncomeQueryModel
    >({
      query: data => ({
        url: `/metering-points/daily-incomes/fill-by-operational-incomes`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'Document',
        'MeteringPoint',
        'DailyIncomes',
        'DailyIncome',
      ],
    }),
    cleanDailyIncomes: builder.mutation<
      void,
      MeteringPointActualIncomeQueryModel
    >({
      query: data => ({
        url: `/metering-points/daily-incomes/clean`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'Document',
        'MeteringPoint',
        'MeteringPoints',
        'DailyIncomes',
        'DailyIncome',
      ],
    }),
  }),
});

export const {
  useQueryByMeteringPointsQuery,
  useQueryByMeteringPointQuery,
  useGetMeteringPointDailyActualIncomeQuery,
  useAddMeteringPointDailyActualIncomeMutation,
  useUpdateMeteringPointDailyActualIncomeMutation,
  useValidateActualIncomeMutation,
  useGetActualIncomeDocumentQuery,
  useGetMeteringPointDailyIncomesQuery,
  useFillByOperationalIncomesMutation,
  useCleanDailyIncomesMutation,
  useVerifyActualIncomeMutation,
  useGetDailyIncomesByDateQuery,
  useSaveDailyIncomesMutation,
  useUpdateDailyIncomesMutation,
  useGetMeteringPointActualIncomesTotalQuery,
} = meteringPointActualIncomeApi;

export const useGetMeteringPointActualIncomesIInfiniteQuery =
  normalizeInfiniteQueryData(
    meteringPointActualIncomeApi.useGetMeteringPointActualIncomesIInfiniteQuery,
  );

const getCachedMPActualIncomeByIdSelector = cachedItemByIdSelector(
  'meteringPointActualIncomeApi',
  'getMeteringPointActualIncomesI',
);

export const useGetCachedMPActualIncomeByIdSelector = id =>
  useSelector(getCachedMPActualIncomeByIdSelector(id));

export const useInvalidateActualIncomeMeteringPointsTags = () => {
  const dispatch = useDispatch();

  const invalidateActualIncomeMeteringPointsTags = useCallback(() => {
    dispatch(
      meteringPointActualIncomeApi.util.invalidateTags([
        'MeteringPoints',
        'MeteringPointsTotal',
      ]),
    );
  }, [dispatch]);

  return {
    invalidateActualIncomeMeteringPointsTags,
  };
};
